import { ChangeDetectionStrategy, Component, forwardRef, ViewChild, ElementRef, Input } from '@angular/core';
import { BASE_COMPONENT, WizBaseComponent } from '../../../core/wiz-base-component';
import { WizTranslatableComponent } from '../../../core/wiz-translatable-component';
import { WizRadioGroup } from '../wiz-radio-group/wiz-radio-group';
import { IWIZ_COMPONENT} from '../../../../interfaces/iwiz-component';
import { VALUE_ENTER, VALUE_SPACE, WIZ_LATERAL } from '@wizeoo/akm-core-lib';
import { WizEventManager } from '../../../../events/wiz-event.manager';

/**
 * Composant bouton radio
 */
@Component({
  selector: 'wiz-radio-button',
  templateUrl: './wiz-radio-button.html',
  styleUrls: ['./wiz-radio-button.scss'],
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizRadioButton) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizRadioButton) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizRadioButton) }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizRadioButton extends WizTranslatableComponent {

  // le composant input véritable
  @ViewChild('innerLabelInput', { static: true, read: ElementRef }) protected _wizInput: ElementRef;
  // Conteneur principal
  @ViewChild('radioHead', { static: true, read: ElementRef }) protected radioHead: ElementRef;

  /** Classe css pour skin "disabled" */
  public disabledClassName: string = 'disabled-class';

  /** Groupe auquel le bouton appartient */
  private _wizGroup: WizRadioGroup;
  public get wizGroup(): WizRadioGroup {
    return this.getWizGroup();
  }
  protected getWizGroup(): WizRadioGroup {
    return this._wizGroup;
  }
  @Input()
  public set wizGroup(pGroup: WizRadioGroup) {
    this.setWizGroup(pGroup);
  }
  protected setWizGroup(pGroup: WizRadioGroup) {
    this._wizGroup = pGroup;
    this.checkStyle();
  }

  /** Nom du groupe de boutons radio */
  public get wizGroupName(): string {
    return this.getWizGroupName();
  }
  protected getWizGroupName(): string {
    return this._wizGroup ? this._wizGroup.wizName : undefined;
  }
  /** Valeur sélectionnée pour le groupe de boutons radio */
  public get wizGroupValue(): string {
    return this.getWizGroupValue();
  }
  protected getWizGroupValue(): string {
    return this._wizGroup ? this._wizGroup.wizValue : undefined;
  }
  public set wizGroupValue(pValue: string) {
    this.setWizGroupValue(pValue);
  }
  protected setWizGroupValue(pValue: string): void {
    // Ici on vient du click sur le bouton radio
    if (this._wizGroup) {
      this.wizGroup.changeButtonValue(pValue);
    }
  }

  /** Le groupe est en erreur (required) */
  public get hasErrors(): boolean {
    return this._wizGroup ? this._wizGroup.hasErrors : false;
  }

  /**
   * @override
   * Priorité au disabled du groupe
   */
  public getWizDisabled(): boolean {
    return this._wizGroup && this._wizGroup.wizDisabled ? true : this._wizDisabled;
  }
  public setWizDisabled(bool: boolean): void {
    super.setWizDisabled(bool);
    this.checkStyle();
  }

  /** label position (before/after), constante WIZ_LATERAL */
  @Input() public set wizLabelPosition(position :string) {
    this.setWizLabelPosition(position);
  }
  protected setWizLabelPosition(position: string): void {
    this._wizLabelPosition = position;
    this.cdref.markForCheck();
  }
  public get wizLabelPosition(): string {
    return this.getWizLabelPosition();
  }
  public getWizLabelPosition(): string {
    let result: string = this._wizLabelPosition;
    if (!result && this._wizGroup) {
      result = this._wizGroup.wizLabelPosition;
    }
    return result;
  }
  public isLabelBefore(): boolean {
    return this.wizLabelPosition === WIZ_LATERAL.BEFORE;
  }
  private _wizLabelPosition: string;

  /** Mise en place de l'écouteur pour saisie clavier */
  public ngAfterViewInit() {
    WizEventManager.addListener(this, this.elementRef, 'keydown', (e: KeyboardEvent) => this.onKeyDown(e));
  }

  /** Suppression de l'écouteur pour saisie clavier */
  public ngOnDestroy() {
    WizEventManager.removeListener(this, this.elementRef, 'keydown');
  }

  /**
   * Sélection de la valeur de ce bouton
   */
  public selectMyValue(): void {
    if (!this.wizDisabled && this.wizGroup && this.wizGroup.wizValue !== this.wizValue) {
      this.wizGroup.changeButtonValue(this.wizValue);
    }
  }

  /** Rafraichissement visuel */
  public refresh(): void {
    this.cdref.markForCheck();
  }

  /* ######## Evenements ######### */

  /**
   * Ecouteur sur key down pour check clavier
   * @param event Evènement key down
   */
  private onKeyDown(event: KeyboardEvent): void {
    // ouverture sur enter / space
    if (event.key === VALUE_SPACE || event.key === VALUE_ENTER) {
      this.selectMyValue();
      event.stopPropagation();
      event.preventDefault();
    }
  }

  /** Prise de focus */
  public wizFocus(): void {
    this.radioHead.nativeElement.focus();
  }

  /** focus > Abonnement du groupe parent au FocusManager */
  public onFocus(): void {
    if (this._wizGroup && !this.wizDisabled) {
      this._wizGroup.wizGetFocusFromButton(this);
    }
  }

  /* ######### STYLE ############# */


  /** application du style effectif (disabled, autres...) */
  public checkStyle(): void {
    // déclenché par des méthodes internes (wizDisabled...)
    if (this.wizDisabled) { // inactif
      this.renderer.addClass(this.elementRef.nativeElement, this.disabledClassName);
      this._wizInput.nativeElement.style.cursor = '';
      this.elementRef.nativeElement.style.cursor = '';
      this.renderer.setAttribute(this.radioHead.nativeElement, 'tabindex', '-1');
    } else { // actif
      this.renderer.removeClass(this.elementRef.nativeElement, this.disabledClassName);
      this._wizInput.nativeElement.style.cursor = 'pointer';
      this.elementRef.nativeElement.style.cursor = 'pointer';
      //this.renderer.setAttribute(this.radioHead.nativeElement, 'tabindex', '0');
    }
  }
}
